import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, ReceiptText, Sparkles } from "lucide-react";
import { useSubscription } from "@/hooks/useBillingAndUsage";
import { useUserCurrency } from "@/hooks/useUserCurrency";
import { useSubscriptionPlans } from "@/hooks/useSubscriptionPlans";
import { SubscriptionModal } from "@/components/billing/SubscriptionModal";
import {
  PLAN_TIERS,
  formatInr,
  formatUsd,
  isCurrentPlan,
} from "@/lib/constants/plans";
import type { PlanTier } from "@/lib/constants/plans";
import type { UserCurrency } from "@/hooks/useUserCurrency";

interface PlanCardProps {
  tier: PlanTier;
  isCurrent: boolean;
  currency: UserCurrency;
  onSelect: (tierName: string) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ tier, isCurrent, currency, onSelect }) => {
  const price =
    currency === 'INR' ? tier.monthlyPriceInr : tier.monthlyPriceUsd;
  const formattedPrice =
    price === null
      ? null
      : currency === 'INR'
        ? formatInr(price)
        : formatUsd(price);

  return (
    <Card
      className={
        isCurrent
          ? "border-2 border-purple-500 shadow-md ring-1 ring-purple-500"
          : tier.highlighted
            ? "border-2 border-purple-200 bg-purple-50/40 hover:border-purple-300 transition-colors"
            : "hover:border-gray-300 transition-colors"
      }
    >
      <CardContent className="flex h-full flex-col p-6">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-bold text-gray-900">{tier.name}</h3>
          {isCurrent ? (
            <Badge className="rounded-full bg-purple-600 text-white hover:bg-purple-600">
              Current Plan
            </Badge>
          ) : tier.highlighted ? (
            <Badge className="rounded-full bg-purple-100 text-purple-700 hover:bg-purple-100">
              Most Popular
            </Badge>
          ) : null}
        </div>
        <p className="mt-1 text-sm text-gray-500">{tier.tagline}</p>

        <div className="mt-4">
          {formattedPrice === null ? (
            <span className="text-3xl font-extrabold text-gray-900">Custom</span>
          ) : (
            <>
              <span className="text-3xl font-extrabold text-gray-900">
                {formattedPrice}
              </span>
              <span className="text-sm text-gray-500"> /month</span>
            </>
          )}
        </div>

        <Separator className="my-4" />

        <ul className="flex-1 space-y-2.5">
          {tier.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
              {feature}
            </li>
          ))}
        </ul>

        {isCurrent ? (
          <Button variant="outline" disabled className="mt-6 w-full">
            Current Plan
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => onSelect(tier.name)}
            className={
              tier.highlighted
                ? "mt-6 w-full bg-purple-600 text-white hover:bg-purple-700 font-semibold"
                : "mt-6 w-full font-medium"
            }
            variant={tier.highlighted ? "default" : "outline"}
          >
            <Sparkles className="mr-1.5 h-4 w-4" />
            {tier.ctaLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export interface BillingPlansSettingsProps {
  onSelectPlan?: (planName: string) => void;
}

export const BillingPlansSettings: React.FC<BillingPlansSettingsProps> = ({
  onSelectPlan,
}) => {
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription();
  const { currency, isLoading: currencyLoading } = useUserCurrency();
  const { data: catalogPlans, isLoading: plansLoading } = useSubscriptionPlans();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string>('Starter');

  const isLoading = subscriptionLoading || currencyLoading || plansLoading;

  const handleSelect = (tierName: string) => {
    if (onSelectPlan) {
      onSelectPlan(tierName);
    } else {
      setSelectedTier(tierName);
      setModalOpen(true);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Plans &amp; Pricing</h2>
        {isLoading ? (
          <Skeleton className="mt-2 h-5 w-64" />
        ) : (
          <p className="mt-1 text-gray-500">
            {subscription
              ? `You're currently on the ${subscription.plan_name}.`
              : "Choose the plan that fits your agency."}{" "}
            Upgrades take effect immediately after confirmation from our team.
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-96 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {PLAN_TIERS.map((tier) => (
            <PlanCard
              key={tier.id}
              tier={tier}
              isCurrent={isCurrentPlan(subscription?.plan_name, tier)}
              currency={currency}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}

      <Card id="billing-history">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-gray-900">Billing History</h3>
          <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-10 text-center">
            <ReceiptText className="h-10 w-10 text-gray-300" />
            <p className="mt-3 font-medium text-gray-700">No invoices yet</p>
            <p className="mt-1 max-w-sm text-sm text-gray-500">
              Invoices will appear here once you subscribe to a paid plan.
            </p>
          </div>
        </CardContent>
      </Card>

      {!onSelectPlan && (
        <SubscriptionModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          targetPlanName={selectedTier}
        />
      )}
    </div>
  );
};
